/**
 * SOP Walkthrough Program service — rolled-out walkthroughs of an SOP.
 * Variant is 'Compliance' or 'Design' (chosen at rollout).
 */
import apiClient from './apiService';
import { GeographySelection } from '../components/common/GeographyPicker';

export type SopVariant = 'Compliance' | 'Design';

export interface SopRolloutPayload {
    variant: SopVariant;
    program_name?: string;
    maker_id: number;
    checker_id: number;
    geography: GeographySelection;
    start_date: string;
    end_date: string;
}

export interface SopProgram {
    id: number;
    entity_id: number;
    sop_id: number;
    walkthrough_template_id?: number | null;
    variant: SopVariant;
    program_name: string;
    program_description?: string | null;
    maker_id?: number | null;
    checker_id?: number | null;
    location_id?: number | null;
    start_date?: string | null;
    end_date?: string | null;
    due_date?: string | null;
    status?: string | null;
    is_active: number;
    is_approved?: number;
    sop_code?: string | null;
    sop_name?: string | null;
    maker_name?: string | null;
    checker_name?: string | null;
    location_name?: string | null;
    step_count?: number;
    completed_count?: number;
    rejection_reason?: string | null;
    rejected_by?: number | null;
    rejected_on?: string | null;
}

export type StepResult = 'Pass' | 'Fail' | 'NA' | 'Complied' | 'NotComplied';

export interface CascadeImpl {
    id: number;
    control_id: number;
    sequence_number?: number | null;
    implementation_type?: string | null;
    target_id?: number | null;
    target_label?: string | null;
    target_subtitle?: string | null;
    explanation?: string | null;
}

export interface CascadeRisk {
    control_id: number;
    risk_id: number;
    risk_code?: string;
    risk_name?: string;
    description?: string;
}

export interface CascadeControl {
    process_step_id: number;
    control_id: number;
    control_code?: string;
    control_name?: string;
    control_type?: string | null;
    risks: CascadeRisk[];
    implementations: CascadeImpl[];
}

/**
 * Saved per-step risk score (GOV_sop_risk_scores). Returned FLAT per step on the
 * Design variant; joined client-side by risk_id. Inherent (likelihood × impact) +
 * risk-level existence/effectiveness drive the client-derived residual. Keyed on
 * step_score_id so the same risk under two steps stays independent.
 */
export interface SopRiskScore {
    id: number;
    step_score_id: number;
    process_step_id: number;
    risk_id: number;
    likelihood_score?: number | null;
    impact_score?: number | null;
    existence_score?: number | null;
    effectiveness_score?: number | null;
    notes?: string | null;
    is_approved?: number;
}

/** Saved per-step, per-risk control score (GOV_sop_control_scores). Existence ×
 *  Effectiveness out of 25. Joined client-side by (risk_id, control_id). */
export interface SopControlScore {
    id: number;
    step_score_id: number;
    risk_id: number;
    control_id: number;
    existence_score?: number | null;
    effectiveness_score?: number | null;
    notes?: string | null;
    is_approved?: number;
}

/** Saved per-step implementation score (GOV_sop_impl_scores). Pass/Fail/NA.
 *  Joined client-side by implementation_id. */
export interface SopImplScore {
    id: number;
    step_score_id: number;
    control_id: number;
    implementation_id: number;
    result?: StepResult | null;
    notes?: string | null;
    is_approved?: number;
}

export interface ProgramStepRow {
    score_id: number;
    process_step_id: number;
    step_number?: number;
    step_name?: string;
    step_description?: string | null;
    step_type?: string | null;
    timeline_days?: number | null;
    timeline_anchor?: string | null;
    authority_id?: number | null;
    authority_name?: string | null;
    result?: StepResult | null;
    remarks?: string | null;
    /** Phase 113 REQ-11 — Effective/Ineffective verdict (Design variant only, added migration 113-02). */
    verdict?: 'Effective' | 'Ineffective' | null;
    is_approved?: number;
    controls?: CascadeControl[];  // only populated for Design variant
    // Saved nested scores (Design variant only) — flat lists joined client-side.
    risk_scores?: SopRiskScore[];
    control_scores?: SopControlScore[];
    impl_scores?: SopImplScore[];
}

export interface SopProgramDetail extends SopProgram {
    steps: ProgramStepRow[];
}

const PROG_BASE = '/api/v1/gov/sop-programs';

export const sopProgramService = {
    async rollout(sopId: number, payload: SopRolloutPayload) {
        const resp = await apiClient.post(`/api/v1/gov/sops/${sopId}/rollout`, payload);
        return resp.data?.data;
    },

    async list(opts?: { assigned_to_me?: boolean; checker_for_me?: boolean; variant?: SopVariant; status?: string; search?: string }): Promise<SopProgram[]> {
        const params: Record<string, string> = { limit: '200' };
        if (opts?.assigned_to_me) params.assigned_to_me = 'true';
        if (opts?.checker_for_me) params.checker_for_me = 'true';
        if (opts?.variant) params.variant = opts.variant;
        if (opts?.status) params.status = opts.status;
        if (opts?.search) params.search = opts.search;
        const resp = await apiClient.get(PROG_BASE, { params });
        return resp.data?.data ?? [];
    },

    async get(id: number): Promise<SopProgramDetail> {
        const resp = await apiClient.get(`${PROG_BASE}/${id}`);
        return resp.data?.data;
    },

    async updateStepScore(
        programId: number,
        scoreId: number,
        payload: { result?: StepResult | null; remarks?: string | null; verdict?: 'Effective' | 'Ineffective' | null },
    ): Promise<void> {
        await apiClient.put(`${PROG_BASE}/${programId}/step-scores/${scoreId}`, payload);
    },

    // Design-variant nested-score upserts (plan 112-01). Each upserts by the
    // table's partial-unique key; the saved rows pre-fill via get_sop_program.
    async saveRiskScore(
        programId: number,
        payload: {
            step_score_id: number;
            process_step_id: number;
            risk_id: number;
            likelihood_score?: number | null;
            impact_score?: number | null;
            existence_score?: number | null;
            effectiveness_score?: number | null;
            notes?: string | null;
        },
    ): Promise<void> {
        await apiClient.put(`${PROG_BASE}/${programId}/risk-scores`, payload);
    },

    async saveControlScore(
        programId: number,
        payload: {
            step_score_id: number;
            risk_id: number;
            control_id: number;
            existence_score?: number | null;
            effectiveness_score?: number | null;
            notes?: string | null;
        },
    ): Promise<void> {
        await apiClient.put(`${PROG_BASE}/${programId}/control-scores`, payload);
    },

    async saveImplScore(
        programId: number,
        payload: {
            step_score_id: number;
            control_id: number;
            implementation_id: number;
            result?: StepResult | null;
            notes?: string | null;
        },
    ): Promise<void> {
        await apiClient.put(`${PROG_BASE}/${programId}/impl-scores`, payload);
    },

    async submit(programId: number): Promise<void> {
        await apiClient.post(`${PROG_BASE}/${programId}/submit`);
    },

    async approve(programId: number): Promise<void> {
        await apiClient.post(`${PROG_BASE}/${programId}/approve`);
    },

    async reject(programId: number, reason: string): Promise<void> {
        await apiClient.post(`${PROG_BASE}/${programId}/reject`, { reason });
    },
};

export default sopProgramService;
